import Route from '@ember/routing/route';
import { later, cancel } from '@ember/runloop';
import { inject as service } from '@ember/service';
import ENV from 'bullitt-time-live/config/environment';

export default class EisaScoringRoute extends Route
{
	@service ajax

	resultsTimer = null
	configTimer = null

	resultsRefreshInterval = 10000
	configRefreshInterval = 60000

	async model()
	{
		const config = await this.fetchConfig();
		const results = await this.fetchAllResults(config);
		return { config, results };
	}

	setupController(controller, model)
	{
		controller.set('event', this.modelFor('event'));
		controller.set('config', model.config);
		controller.set('results', model.results);
	}

	async fetchConfig()
	{
		return await this.ajax.request("eisaScoringConfig.json");
	}

	async fetchAllResults(config = null)
	{
		if (config == null)
		{
			if (this.controller == undefined)
			{
				return {};
			}
			config = this.controller.config;
		}

		// Collect unique results files from nordic races (JSON format)
		const nordicFiles = new Set();
		for (const race of config?.nordic?.races || [])
		{
			nordicFiles.add(race.resultsFile);
		}

		// Collect unique results files from alpine races (FIS XML format)
		const alpineFiles = new Set();
		for (const race of config?.alpine?.races || [])
		{
			alpineFiles.add(race.resultsFile);
		}

		const results = {};

		// Fetch nordic JSON files
		for (const fileName of nordicFiles)
		{
			let data = await this.ajax.request(fileName);
			data = data.map(r => r.data.attributes);
			results[fileName] = data;
		}

		// Fetch and parse alpine XML files
		for (const fileName of alpineFiles)
		{
			const response = await fetch(ENV.APP.ajaxHost + fileName, { cache: "no-store" });
			const xmlText = await response.text();
			results[fileName] = this.parseFisXml(xmlText);
		}

		return results;
	}

	parseFisXml(xmlText)
	{
		const parser = new DOMParser();
		const doc = parser.parseFromString(xmlText, 'text/xml');
		const results = [];

		// Only get AL_ranked elements (finishers with status="QLF")
		const rankedElements = doc.querySelectorAll('AL_ranked');

		for (const ranked of rankedElements)
		{
			const status = ranked.getAttribute('Status');
			if (status !== 'QLF') continue;

			const rank = ranked.querySelector('Rank')?.textContent;
			const competitor = ranked.querySelector('Competitor');
			const alResult = ranked.querySelector('AL_result');

			if (!competitor || !alResult) continue;

			const totalTime = alResult.querySelector('Totaltime')?.textContent;
			if (!totalTime) continue;

			const lastName = competitor.querySelector('Lastname')?.textContent || '';
			const firstName = competitor.querySelector('Firstname')?.textContent || '';
			const club = competitor.querySelector('Clubname')?.textContent || '';

			results.push({
				place: parseInt(rank, 10),
				firstName: firstName,
				lastName: lastName,
				club: club,
				totalTime: totalTime,
				status: 'OK'
			});
		}

		return results;
	}

	async fetchResultsLoop()
	{
		cancel(this.resultsTimer);
		this.resultsTimer = later(this, async function()
		{
			const results = await this.fetchAllResults();
			this.controller.set('results', results);

			this.fetchResultsLoop();
		}, this.resultsRefreshInterval);
	}

	async fetchConfigLoop()
	{
		cancel(this.configTimer);
		this.configTimer = later(this, async function()
		{
			const config = await this.fetchConfig();
			this.controller.set('config', config);

			this.fetchConfigLoop();
		}, this.configRefreshInterval);
	}

	activate(transition)
	{
		this.fetchResultsLoop();
		this.fetchConfigLoop();
	}

	deactivate(transition)
	{
		cancel(this.resultsTimer);
		cancel(this.configTimer);
	}
}
